import classNames from "classnames";
import escapeRegExp from "lodash/escapeRegExp";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import PropTypes from "prop-types";
import React from "react";
import { Row, Col, FormControl } from "react-bootstrap";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

class Table extends React.Component {
  static get displayName() {
    return "Table";
  }

  static get propTypes() {
    return {
      actions: PropTypes.node,
      className: PropTypes.string,
      cellEdit: PropTypes.object,
      children: PropTypes.node,
      condensed: PropTypes.bool,
      data: PropTypes.array,
      defaultSorted: PropTypes.array,
      exportFile: PropTypes.string,
      exportFunc: PropTypes.func,
      hover: PropTypes.bool,
      keyField: PropTypes.string,
      onTableChange: PropTypes.func,
      options: PropTypes.object,
      pagination: PropTypes.bool,
      remote: PropTypes.bool,
      rowClasses: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
      search: PropTypes.bool,
      selectRow: PropTypes.object,
      setRef: PropTypes.func,
      toolbar: PropTypes.element,
      toolbarWidth: PropTypes.number,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      keyword: null,
    };
    this.sizePerPageBtnRef = React.createRef();
  }

  componentDidMount() {
    const btn = this.sizePerPageBtnRef.current;
    if (btn) {
      btn.blur();
    }
  }

  _test(v, re) {
    if (isArray(v)) {
      return this._inArray(v, re);
    } else if (isObject(v)) {
      return this._inObject(v, re);
    } else if (!isEmpty(v)) {
      return re.test(v);
    }

    return false;
  }

  _inObject(obj, re) {
    if (isEmpty(obj)) {
      return false;
    }

    for (let [, v] of Object.entries(obj)) {
      if (this._test(v, re)) {
        return true;
      }
    }

    return false;
  }

  _inArray(arr, re) {
    if (isEmpty(arr)) {
      return false;
    }

    for (let row of arr) {
      if (this._test(row, re)) {
        return true;
      }
    }

    return false;
  }

  _search(rows, kw) {
    const data = [];
    if (!kw) {
      return data;
    }

    const re = new RegExp(escapeRegExp(kw), "i");
    for (let row of rows) {
      if (this._test(row, re)) {
        data.push(row);
      }
    }

    return data;
  }

  _handleSearch(e) {
    const keyword = e.currentTarget.value;
    this.setState({ keyword: keyword });
  }

  _getData() {
    const baseData = this.state.keyword
      ? this._search(this.props.data, this.state.keyword)
      : this.props.data;

    return baseData.map((row, index) => ({
      ...row,
      _uniqueKey: `${row.id || "row"}-${index}`,
    }));
  }

  _escape(str) {
    let nstr = str;
    if (typeof str !== "string") {
      nstr = JSON.stringify(str);
    }

    return nstr.replace(/"/g, '\\"');
  }

  _exportDataUri() {
    if (isEmpty(this.props.data) || !this.props.exportFunc) {
      return `data:text/csv;charset=utf-8,${encodeURIComponent("No data")}`;
    }

    const data = this.props.exportFunc();
    const encoded = [];

    for (let row of data) {
      const erow = [];
      for (let v of row) {
        if (v) {
          erow.push(`"${this._escape(v)}"`);
        }
      }

      encoded.push(erow.join(","));
    }

    const dataStr = encoded.join("\r\n");
    return window.URL.createObjectURL(
      new window.Blob([dataStr], { type: "text/csv" }),
    );
  }

  _sortCaret(order, column) {
    if (!order) {
      return <span className="fa fa-sort ml-2" />;
    } else if (order === "asc") {
      return <span className="fa fa-sort-asc ml-2" />;
    } else if (order === "desc") {
      return <span className="fa fa-sort-desc ml-2" />;
    }
  }

  _convertColumnsFromChildren(children) {
    return React.Children.map(children, (child) => {
      if (!child || child.type.displayName !== "Table.Column") return null;

      const {
        children: label,
        dataAlign,
        dataField,
        dataFormat,
        dataSort,
        tdStyle,
        width,
        ...restProps
      } = child.props;
      const align = dataAlign || restProps.align;

      return {
        align,
        dataField: dataField || "",
        formatter: dataFormat,
        headerAlign: align,
        headerStyle: width ? { width: width } : undefined,
        style: tdStyle,
        sort: dataSort,
        sortCaret: dataSort ? this._sortCaret.bind(this) : undefined,
        text: label || "",
        ...restProps,
      };
    }).filter(Boolean);
  }

  _getDefaultSorted(defaultSorted, options) {
    if (defaultSorted) {
      return defaultSorted;
    }

    if (options && (options.sortName || options.defaultSortName)) {
      return [
        {
          dataField: options.sortName || options.defaultSortName,
          order: options.sortOrder || options.defaultSortOrder || "asc",
        },
      ];
    }

    return [];
  }

  _getNoDataIndication(options) {
    if (options && options.noDataText) {
      return options.noDataText;
    }

    return () => "There is no data to display";
  }

  _renderPaginationDropDown(opts) {
    return (
      <div className="btn-group" role="group">
        <button
          aria-haspopup="true"
          aria-expanded="false"
          className="btn btn-secondary dropdown-toggle"
          data-toggle="dropdown"
          id="pageDropDown"
          ref={this.sizePerPageBtnRef}
          type="button"
        >
          {opts.currSizePerPage}
        </button>
        <div className="dropdown-menu">
          {opts.options.map((opt, index) => (
            <button
              className={`dropdown-item ${
                opt.page === opts.currSizePerPage ? "active" : ""
              }`}
              onClick={() => opts.onSizePerPageChange(opt.page)}
              key={index}
              type="button"
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  _renderPaginationTotal(from, to, size) {
    return (
      <span className="pagination-total mx-2">
        {`${from} - ${to} of ${size}`}
      </span>
    );
  }

  render() {
    const {
      actions,
      cellEdit,
      children,
      className,
      condensed,
      // eslint-disable-next-line no-unused-vars
      data, // Don't overwrite the data prop on the table.
      defaultSorted,
      exportFile,
      hover,
      keyField,
      options,
      pagination,
      remote,
      rowClasses,
      toolbar,
      toolbarWidth,
      search,
      selectRow,
      setRef,
      ...rest
    } = this.props;

    const inputClasses = classNames({
      "search-box": true,
      "pull-right": true,
      hide: !search,
    });

    const exportClasses = classNames({
      "pull-right": true,
      btn: true,
      "btn-primary": true,
      "export-button": true,
      hide: !exportFile,
    });

    const bsTableProps = {
      cellEdit,
      condensed,
      defaultSorted: this._getDefaultSorted(defaultSorted, options),
      hover,
      options,
      noDataIndication: this._getNoDataIndication(options),
      rowClasses,
      selectRow,
      ...rest,
    };

    const { onRowClick, ...otherOptions } = options || {};
    if (typeof onRowClick === "function") {
      bsTableProps.rowEvents = {
        onClick: (e, row, rowIndex) => {
          onRowClick(row, rowIndex, e);
        },
      };
    }
    bsTableProps.options = otherOptions;
    const tableData = remote ? data : this._getData();
    const resolvedColumns = this._convertColumnsFromChildren(children);

    const paginationOptions = options?.pagination || {};
    const paginationProps = pagination
      ? paginationFactory({
          ...paginationOptions,
          hideSizePerPage: options?.hideSizePerPage
            ? options.hideSizePerPage
            : false,
          onPageChange: options?.onPageChange,
          onSizePerPageChange: options?.onSizePerPageChange,
          paginationTotalRenderer: this._renderPaginationTotal.bind(this),
          showTotal: options?.hideShowTotal ? false : true,
          sizePerPage: options?.sizePerPage || 10,
          sizePerPageList: options?.sizePerPageList || [
            { text: "10", value: 10 },
            { text: "25", value: 25 },
            { text: "50", value: 50 },
          ],
          sizePerPageRenderer: this._renderPaginationDropDown.bind(this),
          totalSize: options?.fetchInfo && options.fetchInfo.dataTotalSize,
        })
      : undefined;

    return (
      <div className={`table-containers ${className}`}>
        <Row className="table-controllers">
          <Col xs={toolbarWidth || 8}>{toolbar}</Col>
          {!actions && (
            <Col xs={4}>
              <a
                href={this._exportDataUri()}
                download={exportFile}
                className={exportClasses}
                title="Export"
              >
                <i className="fa fa-download" />
              </a>
              <FormControl
                type="text"
                className={inputClasses}
                onChange={this._handleSearch.bind(this)}
                placeholder="Search"
              />
            </Col>
          )}
        </Row>
        {actions && (
          <Row className="table-controllers">
            <Col xs={8}>{actions}</Col>
            <Col xs={4}>
              <FormControl
                type="text"
                className="search-box pull-right"
                onChange={this._handleSearch.bind(this)}
                placeholder="Search"
              />
            </Col>
          </Row>
        )}
        <BootstrapTable
          columns={resolvedColumns}
          data={this._getData()}
          keyField="_uniqueKey"
          onTableChange={this.props.onTableChange}
          pagination={paginationProps}
          remote={remote}
          ref={setRef}
          {...bsTableProps}
        />
      </div>
    );
  }
}

class Column extends React.Component {
  static get displayName() {
    return "Table.Column";
  }

  render() {
    return null;
  }
}

export default { Table, Column };
export { Table, Column };
